import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ArmGlyph, IyafWordmark } from "@/components/brand/IyafMark";
import { arms, stories, org } from "@/data/content";
import ArmsNav from "@/components/site/ArmsNav";

const hub = arms.find((a) => a.key === "hub");
const hubStories = stories.filter((s) => s.arm === "hub");

const FACILITY_KEYS = ["totalArea", "trainingRoom", "meetingRooms", "eventSpace", "coworking", "tech"];
const PROGRAM_KEYS = ["legal", "experts", "events"];

export default function Hub() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <section className="pt-16 pb-12 relative overflow-hidden">
        <div className="container-iyaf relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <ArmGlyph color={hub.color} className="w-14 h-14" />
              <div>
                <p className="small-caps text-xs text-iyaf-blue">{t("armCards.active")}</p>
                <p className="text-sm text-iyaf-ink/55">{t("armCards.hub.tagline")}</p>
              </div>
            </div>
            <IyafWordmark variant="hub" size="hero" className="mb-6" />
            <h1 className="font-display font-black tracking-tighter leading-[0.95] max-w-3xl text-balance text-4xl sm:text-5xl">
              <span className="font-editorial italic font-normal text-iyaf-ink/85">{t("armPages.hub.heroTitle")}</span>
            </h1>
            <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-2xl">
              {t("armPages.hub.heroBody")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-[40px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&w=1200"
                alt="Youth working in the Hub"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-iyaf-ink/85 to-transparent">
                <p className="small-caps text-[10px] text-white/70">{t("armPages.hub.locationRegion")}</p>
                <p className="font-display text-white text-xl font-black">{t("armPages.hub.locationTitle")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-iyaf">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-black/10 rounded-3xl overflow-hidden border border-black/5">
            {FACILITY_KEYS.map((key) => (
              <div key={key} className="bg-white p-5">
                <p className="font-display text-2xl font-bold text-iyaf-blue">{t(`armPages.hub.facility.${key}.value`)}</p>
                <p className="text-[11px] uppercase tracking-widest text-iyaf-ink/55 mt-1">{t(`armPages.hub.facility.${key}.label`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-iyaf grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            {PROGRAM_KEYS.map((key) => (
              <div key={key}>
                <p className="eyebrow text-iyaf-blue mb-3">{t(`armPages.hub.programs.${key}.eyebrow`)}</p>
                <h2 className="font-display text-3xl font-bold mb-4">{t(`armPages.hub.programs.${key}.title`)}</h2>
                <p className="text-iyaf-ink/75 leading-relaxed">{t(`armPages.hub.programs.${key}.body`)}</p>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {hubStories.map((s) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, x: 14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5 }}
                className="p-5 rounded-2xl bg-white border border-black/5 flex gap-4"
                data-testid={`hub-story-${s.slug}`}
              >
                <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-display text-lg font-bold">{s.name}</p>
                  <p className="text-xs text-iyaf-ink/65 mt-1 line-clamp-2">{t(`storiesDeep.list.${s.slug}.summary`, s.summary)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-iyaf-blue font-mono mt-2">→ {t(`storiesDeep.list.${s.slug}.impact`, s.impact)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-iyaf-blue text-white relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-[420px] h-[420px] rounded-full border border-white/15 animate-spin-slow" />
        <div className="container-iyaf relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow text-white/60 mb-4">{t("armPages.hub.visit.eyebrow")}</p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mb-6">{org.hq}</h2>
            <p className="text-white/75 mb-8 max-w-md">
              {t("armPages.hub.visit.tourIntro")}{" "}
              <a className="underline" href={`mailto:${org.email}`}>{org.email}</a>
              <br />
              {t("armPages.hub.visit.orCall")} {org.phone}.
            </p>
            <Link to="/login" data-testid="hub-booking-cta" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-iyaf-blue font-semibold hover:bg-iyaf-paper transition">
              {t("armPages.hub.visit.bookCta")} <ArrowRight size={18} />
            </Link>
          </div>
          <div className="rounded-3xl bg-white/5 border border-white/15 p-8">
            <p className="eyebrow text-white/50 mb-4">{t("armPages.hub.inside.eyebrow")}</p>
            <ul className="space-y-3 text-white/85">
              {t("armPages.hub.inside.items", { returnObjects: true }).map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <MapPin size={16} /> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <ArmsNav currentKey="hub" />
    </div>
  );
}
